import { z } from 'zod';
import { knownLeadSourceSchema } from './schemas/crm';
import { bandIntervallSchema, tenantSchema } from './schemas/valuation';
import { calendarEventTypeSchema } from './schemas/calendar';
import { checklistSectionSchema } from './schemas/pipeline';
import { VOR_ORT_ERREICHBARKEITEN, VOR_ORT_OBJEKTARTEN, VOR_ORT_ZEITRAEUME, } from './schemas/vor-ort';
import { OBJEKT_ANFRAGE_ANLIEGEN } from './schemas/objekt-anfrage';
/**
 * Internal event bus contract (backend → n8n, HMAC-signed).
 * n8n orchestrates side effects (email, reminders, bank handoffs); it never
 * writes the database. Each event is a signed envelope with a typed payload.
 */
export const EVENT_TYPES = [
    'lead.created',
    'result.email',
    'consult.requested',
    'portal.invited',
    'auth.password_reset',
    'auth.login_alert',
    'dossier.completed',
    'reminder.due',
    'rates.stale',
    'rates.scrape_failed',
    'rates.scrape_anomaly',
    'valuation.lead.created',
    'valuation.estimated',
    'booking.created',
    'contract.sent',
    'mandate.signed',
    'shooting.proposed',
    'shooting.confirmed',
    'mandate_change.proposed',
    'calendar.event_invited',
    'break_glass.granted',
    'break_glass.expired',
    'display_token.erstellt',
    'display_token.widerrufen',
    'user.created',
    'user.invited',
    'user.updated',
    'user.deactivated',
    'drip.eingabe.erstellt',
    // G3 (owner 21-8): the funnel's global SMS day budget closed — counts only.
    'valuation.sms_budget_exceeded',
    // Fase A4 (owner 26-8): a funnel Bewertungsanfrage became a dossier — ids only.
    'bewertung.uebernommen',
    // Fase B3 (owner 26-8): the ONE reminder for an unopened Bewertungsbericht.
    'lead.report_reminder',
    // Kommunikation PR-2 (dossier §1.1): the staff twin of `auth.password_reset` —
    // a colleague asked for a new password via «Passwort vergessen»; carries the
    // single-use link, so it is scrubbed like `user.invited`.
    'user.password_reset',
    // Vermarktung A-4 (ontwerp §2.8): iemand vroeg op een publieke listing de
    // Verkaufsdokumentation aan. Draagt de tijdgebonden token-link, dus
    // `highly_confidential` en de duurzame kopie wordt geschrobd — het
    // `contract.sent`-precedent, om exact dezelfde reden.
    'expose.requested',
    // Vor-Ort-Bewertung («Immobilie verkaufen», contract docs/VOR-ORT-FORMULIER-CONTRACT.md
    // §5.1): a seller asked for a free on-site valuation — no telephone number on it.
    'vor_ort.requested',
    // Order E1 (17-10): de Objekt-Anfrage (Besichtigung/Unterlagen) op een
    // Kauf-Objektseite. PII-VRIJ: ids, het anliegen en de testvlag — naam,
    // telefoon en nachricht leest de render-service uit `objekt_anfragen`.
    'objekt_anfrage.requested',
];
export const eventTypeSchema = z.enum(EVENT_TYPES);
// ── Enrichment (optional on every payload) ───────────────────────────────────
// Emit sites add the data a mail template needs so n8n can personalise its
// side-effects WITHOUT reading the database (n8n stays side-effects-only).
// Everything here is optional and additive: the n8n router degrades to an
// internal notice when a field it needs is absent, so old and new producers
// can coexist. Deliberately no max-length bounds — a too-long source string
// must never make an emit (and thereby the originating request) fail.
const nameFields = {
    firstName: z.string().optional(),
    lastName: z.string().optional(),
};
/** Mail-relevant core numbers of a calculator run (E1 + the internal notify). */
export const calculatorSummarySchema = z
    .object({
    verdict: z.string().optional(),
    propertyValue: z.number().optional(),
    monthlyCost: z.number().optional(),
    affordabilityRatioPct: z.number().optional(),
    loanToValuePct: z.number().optional(),
})
    .strict();
// ── Per-event payloads ───────────────────────────────────────────────────────
//
// EVERY payload object below is STRICT, nested ones included — an unknown key is
// a refusal, not a silent strip. The two break-glass payloads further down said
// this first (C6-13); this is the same rule applied to the other nineteen, for
// the same reason, so the file has ONE answer to "may a surplus field travel".
//
// WHY, because "zod strips unknown keys" reads like it already handled this
// (review of PR #319). Two facts turned the strip into a pass-through:
//
//   1. `refineEnvelope` (below) only asks `safeParse(...).success`. Stripping IS
//      a success, so an extra field never produced a single issue.
//   2. `EventsService.emit()` signs and stores `JSON.stringify(envelope)` — the
//      ORIGINAL object, not zod's stripped copy. The parse was a gate, never a
//      filter, and nothing downstream ever saw the cleaned version.
//
// So whatever a producer put next to the contract went verbatim to n8n AND into
// the durable `webhook_events` row — which has no retention, is replayed
// byte-for-byte and is read in the Control Center. That is the shape in which a
// debug field, an internal note or a whole ORM record ends up in a mail template
// and an audit copy, permanently, from a diff that looks like one added property.
//
// Strict makes it a REFUSAL at emit time: `emit()` throws on an invalid envelope,
// so the surplus field fails at its own call site and in its own test rather than
// arriving somewhere it can no longer be taken back from. This binds the WRITE
// path only — see `eventEnvelopeReadSchema` for why stored rows keep reading
// whatever they were written with.
//
// The nested objects are strict for the same reason as the top level: a surplus
// key one level down travels exactly as far, and strictness that stopped at the
// surface would be a rule with a hole in the middle.
export const leadCreatedSchema = z
    .object({
    leadId: z.string().uuid(),
    email: z.string().email(),
    locale: z.string(),
    calculatorType: z.string(),
    ...nameFields,
    source: z.string().optional(),
    calculator: calculatorSummarySchema.optional(),
})
    .strict();
export const resultEmailSchema = z
    .object({
    leadId: z.string().uuid(),
    email: z.string().email(),
    locale: z.string(),
    ...nameFields,
    calculator: calculatorSummarySchema.optional(),
})
    .strict();
export const consultRequestedSchema = z
    .object({
    leadId: z.string().uuid().optional(),
    customerAccountId: z.string().uuid().optional(),
    preferredAt: z.string().datetime().optional(),
    channel: z.enum(['phone', 'video', 'office']).default('phone'),
    email: z.string().email().optional(),
    ...nameFields,
    advisorName: z.string().optional(),
    // Callback-form enrichment (POST /ingest/beratung): the event is the only
    // transport to the advisors, so it carries what they need to call back.
    phone: z.string().optional(),
    /** Free-text wish from the form ("vormittag", …) — not a datetime. */
    preferredTime: z.string().optional(),
    /** Caller's requested language (de/fr/it/en) from the form. */
    language: z.string().optional(),
    /**
     * Which tool/surface triggered the request ('rechner' | 'vergleich', W6b).
     * Additive + nullable: old producers/consumers (n8n flows) keep working.
     */
    triggerSource: z.string().nullable().optional(),
    /**
     * Tenant the request belongs to (MLG Verkauf Beratungs-widget → 'mlg');
     * the router brands the confirmation mail + team notice on it. Additive.
     */
    tenant: z.enum(['finavest', 'mlg']).optional(),
    /** Beratungs-widget topic (enum on the wire — never free text). */
    topic: z.string().optional(),
    address: z
        .object({
        street: z.string().optional(),
        zip: z.string().optional(),
        city: z.string().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const portalInvitedSchema = z
    .object({
    customerAccountId: z.string().uuid(),
    inviteId: z.string().uuid(),
    email: z.string().email(),
    /** Full single-use action URL the backend already built (n8n only templates it). */
    actionUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    ...nameFields,
})
    .strict();
export const authPasswordResetSchema = z
    .object({
    customerAccountId: z.string().uuid(),
    email: z.string().email(),
    /** Full single-use reset URL the backend already built (n8n only templates it). */
    actionUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    ...nameFields,
})
    .strict();
/**
 * "Neue Anmeldung" warning to the advisor's own address: a login from an
 * unknown device, or a known device token showing up from a clearly different
 * IP (hash mismatch). Informational only — the login itself is never blocked.
 */
export const authLoginAlertSchema = z
    .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    reason: z.enum(['new_device', 'new_ip']),
    /** Human-readable device ("Chrome · Windows"); UA-derived, no fingerprint. */
    deviceLabel: z.string().optional(),
    /** Indicative location only (Cloudflare country code) — never a raw IP. */
    location: z.string().optional(),
    loginAt: z.string().datetime(),
})
    .strict();
export const dossierCompletedSchema = z
    .object({
    financingRequestId: z.string().uuid(),
    customerAccountId: z.string().uuid(),
    email: z.string().email().optional(),
    ...nameFields,
    advisorName: z.string().optional(),
})
    .strict();
export const reminderDueSchema = z
    .object({
    kind: z.string(),
    targetType: z.enum(['lead', 'financing_request', 'consultation', 'task']),
    targetId: z.string().uuid(),
    dueAt: z.string().datetime(),
    email: z.string().email().optional(),
    ...nameFields,
    /** Dossier progress for E4: pct = still open; the consumer derives done_pct. */
    pct: z.number().min(0).max(100).optional(),
    missingCount: z.number().int().optional(),
    topMissing: z.string().optional(),
    portalUrl: z.string().url().optional(),
})
    .strict();
// ── W6a Vergleicher events (internal routing only — n8n decides delivery) ───
/**
 * Daily digest of partners whose CRM rate sheet is older than the public
 * degradation window. INTERNAL advisor reminder — partner labels must never
 * reach a public surface.
 */
export const ratesStaleSchema = z
    .object({
    /** Days after which the public Finavest aggregate degrades (10). */
    maxAgeDays: z.number().int(),
    partners: z
        .array(z
        .object({
        label: z.string(),
        /** Age of the partner's freshest rate row, in whole days. */
        days: z.number().int(),
    })
        .strict())
        .min(1),
})
    .strict();
/** A scrape source failed to fetch or parse (→ NOTIFY_OPS). */
export const ratesScrapeFailedSchema = z
    .object({
    providerKey: z.string(),
    label: z.string(),
    url: z.string().optional(),
    error: z.string(),
    consecutiveFailures: z.number().int(),
    /** true when the source was auto-disabled (e.g. HTTP 403 — never bypass). */
    disabled: z.boolean().optional(),
})
    .strict();
/**
 * Sanity-guard rejections of one source, aggregated per run (→ NOTIFY_OPS).
 * One event per source per run — never one per withheld value (dedupe lives
 * at the producer). `items` is capped at 20; `anomalies` is the full count.
 */
export const ratesScrapeAnomalySchema = z
    .object({
    providerKey: z.string(),
    label: z.string(),
    anomalies: z.number().int().min(1),
    items: z
        .array(z
        .object({
        product: z.string(),
        termYears: z.number().int().nullable(),
        newRate: z.number(),
        previousRate: z.number().optional(),
        reason: z.enum(['out_of_range', 'jump']),
    })
        .strict())
        .min(1)
        .max(20),
})
    .strict();
// ── Immobilien-Bewertung event (M0+) ─────────────────────────────────────────
/**
 * A valuation funnel produced a lead (map + Bewertung tool). Internal routing
 * only: n8n decides delivery and any cross-sell handoff. `tenant` is the brand
 * the lead belongs to; `crossSell` flags an MLG↔Finavest hand-over opportunity
 * (e.g. an MLG valuation lead that also fits a Finavest mortgage). No PII
 * beyond the lead id — n8n resolves contact data from the lead it routes.
 */
export const valuationLeadCreatedSchema = z
    .object({
    tenant: tenantSchema,
    leadId: z.string().uuid(),
    source: knownLeadSourceSchema,
    crossSell: z.boolean(),
})
    .strict();
/**
 * The instant AVM produced a value for a phone-verified valuation lead (M5).
 * Routed to the E8 result-mail. PII-free by design — n8n resolves the recipient
 * via the internal contact lookup (`POST /internal/valuation/contact`). The
 * monetary range IS allowed (no PII), so the mail can template the Bewertung
 * without a second round-trip. `source` is the tenant-determined funnel source
 * (same set as `valuation.lead.created`).
 */
export const valuationEstimatedSchema = z
    .object({
    tenant: tenantSchema,
    leadId: z.string().uuid(),
    source: knownLeadSourceSchema,
    currency: z.string(),
    estimate: z.number().nonnegative(),
    /** B10: the COMMUNICATED band — IAZI's 50 % interval, or the 95 % when 50 is missing (`intervall`). */
    low: z.number().nonnegative(),
    high: z.number().nonnegative(),
    /**
     * B10 (master 29-9), additive + optional (n8n ignores unknown keys): IAZI's
     * 95 % interval raw, which interval `low`/`high` carry, and IAZI's
     * `qualityCode` (spec 0…4; absent for the stub). `low95`/`high95` are money —
     * the durable copy strips them with `low`/`high` (`scrubbed-envelopes.ts`).
     *
     * `qualityCode` is typed like the dossier column it mirrors (`avmQualityCode
     * Int?`) and deliberately WITHOUT a range (RVB10 MINOR-1): this event is
     * emitted inside the estimate transaction, AFTER the paid model call — a
     * bound here would roll a paid estimate back over a label. A code outside
     * IAZI's scale is a display question (`iaziQualitaetLabel` → «Code n»).
     */
    low95: z.number().nonnegative().optional(),
    high95: z.number().nonnegative().optional(),
    intervall: bandIntervallSchema.optional(),
    qualityCode: z.number().int().optional(),
    confidence: z.number().min(0).max(1),
    provider: z.string(),
    /**
     * Fase B (owner 26-8): what the MAIL shows — the same band as the result
     * screen (`bandbreite()`), never the point value — and the «Bericht
     * öffnen» link. `actionUrl` IS the credential; that name is what makes
     * `durableCopy` redact `token=…` in the stored copy, like `portal.invited`.
     * The band is stripped from the durable copy with the other amounts.
     */
    bandLow: z.number().nonnegative(),
    bandHigh: z.number().nonnegative(),
    /**
     * OPTIONEEL, en precies één reden waarom (review #492 MAJOR-5): met de
     * release-rem `LEAD_BERICHT_LINK_AKTIV=false` draagt de mail wel de band
     * maar GÉÉN knop. De Bericht-pagina komt uit PR #494 en de mailvorm uit de
     * n8n-herimport; rijdt de backend-trein alleen, dan zou elke klant een knop
     * krijgen naar een pagina die nog niet bestaat. De router laat het
     * rapport-blok weg zodra dit veld ontbreekt — nooit een dode knop.
     * Aanwezig ⇒ het IS het credential, en die naam is wat `durableCopy`
     * `token=…` laat redigeren, net als bij `portal.invited`.
     */
    actionUrl: z.string().url().optional(),
    /** Whole days the link is valid — from the real expiry, so the mail never promises more. */
    gueltigTage: z.number().int().positive().optional(),
    /**
     * B3-5b: the same customer-facing quality note the result screen shows
     * (`IAZI_QUALITAET_HINWEIS_OEFFENTLICH`, only for IAZI quality 1/4) — the
     * router hands it to E8 as `qualitaet_hinweis`. Text, no amount, no PII.
     */
    qualitaetHinweis: z.string().min(1).max(300).optional(),
})
    .strict();
/**
 * Fase B3 (owner 26-8): the customer did not open the Bewertungsbericht within
 * LEAD_REPORT_REMINDER_NACH_STUNDEN → exactly ONE reminder with a FRESH link
 * (the first link stays valid until its own expiry; the fresh one is born
 * reminded so it can never trigger a second reminder). Same shape as the E8
 * event minus the point value: the reminder never needs it. Contact resolved
 * by the router's lookup sub-branch, like E8.
 */
export const leadReportReminderSchema = z
    .object({
    tenant: tenantSchema,
    leadId: z.string().uuid(),
    source: knownLeadSourceSchema,
    currency: z.string(),
    bandLow: z.number().nonnegative(),
    bandHigh: z.number().nonnegative(),
    actionUrl: z.string().url(),
    gueltigTage: z.number().int().positive(),
    /** #579 MINOR-3: the same customer note E8 carried (1/4 = indicative); absent for 2/3 and stub rows. */
    qualitaetHinweis: z.string().min(1).max(300).optional(),
})
    .strict();
/**
 * G3 (owner 21-8): the GLOBAL SMS day budget of the Bewertungs-funnel is
 * exhausted — the code step is closed for the rest of the UTC day. Emitted
 * EXACTLY ONCE per day (the caps service's SETNX crossed-tick raises it).
 * PII-free by construction: counters only — no phone, no lead, no e-mail.
 */
export const valuationSmsBudgetExceededSchema = z
    .object({
    /** UTC day the budget closed (YYYY-MM-DD). */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Attempted sends at the moment of crossing (budget + 1). */
    count: z.number().int().positive(),
    /** The configured VALUATION_SMS_DAILY_BUDGET at that moment. */
    budget: z.number().int().positive(),
})
    .strict();
/**
 * Fase A4 (owner 26-8): a funnel lead's Bewertungsanfrage was taken over into a
 * Bewertungs-DOSSIER (`valuation_requests.origin='uebernommen'`) — by an
 * advisor's «In Bewertung übernehmen» or automatically on a booked
 * consultation. Ids only: no amounts (the durable copy would otherwise need
 * the money scrub `valuation.estimated` has), no contact data. `via` says which
 * of the two paths did it, because the team notice reads differently for each.
 */
export const bewertungUebernommenSchema = z
    .object({
    tenant: tenantSchema,
    leadId: z.string().uuid(),
    /** The new dossier row. */
    valuationRequestId: z.string().uuid(),
    /** The funnel request it was copied from. */
    quelleId: z.string().uuid(),
    via: z.enum(['advisor', 'booking']),
})
    .strict();
/**
 * B: a verified funnel lead booked a phone consultation (funnel end step).
 * PII-free — ids + the slot only; the advisor resolves contact data in the CRM.
 */
export const bookingCreatedSchema = z
    .object({
    tenant: tenantSchema,
    leadId: z.string().uuid(),
    consultationId: z.string().uuid(),
    /** Confirmed slot start, ISO-8601 UTC. */
    scheduledAt: z.string().datetime(),
    durationMin: z.number().int().positive(),
})
    .strict();
// ── MLG Verkaufs-Pipeline events (F4) ────────────────────────────────────────
/**
 * The Maklervertrag was sent to the owner for signature (built-in SES). Carries
 * the full single-use sign URL the backend already built (n8n only templates it
 * into the mail) — like `portal.invited`, the token is REDACTED in the audit copy.
 */
export const contractSentSchema = z
    .object({
    mandateId: z.string().uuid(),
    contractId: z.string().uuid(),
    email: z.string().email(),
    /** Full single-use sign URL (my.finavest.ch/mandat-signieren?token=…). */
    actionUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    objektAdresse: z.string().optional(),
    ...nameFields,
})
    .strict();
/**
 * The owner signed the Maklervertrag → it is now a real mandate (internal
 * advisor notification). PII-minimal: the advisors resolve the rest from the
 * mandate.
 *
 * `customerAccountId` IS OPTIONAL (additive, owner dossier 26-08-2026 §6).
 * Signing no longer creates a portal account, so a signed mandate may simply
 * have none — staff invite the owner from the CRM afterwards. Before, the
 * emitter skipped this event entirely when there was no account, which would
 * have turned "owner has no portal access yet" into "the signature produced no
 * event at all". Widening the field is the additive half of that fix; every
 * stored event and every existing consumer keeps working, since a producer that
 * has the id still sends it.
 */
export const mandateSignedSchema = z
    .object({
    mandateId: z.string().uuid(),
    contractId: z.string().uuid(),
    customerAccountId: z.string().uuid().optional(),
    signerName: z.string(),
    signedAt: z.string().datetime(),
    objektAdresse: z.string().optional(),
})
    .strict();
/**
 * Staff proposed a Foto-Shooting date → the owner approves it in the portal.
 * Carries the portal URL so n8n can template the "bitte bestätigen" mail.
 */
export const shootingProposedSchema = z
    .object({
    mandateId: z.string().uuid(),
    shootingId: z.string().uuid(),
    proposedAt: z.string().datetime(),
    email: z.string().email().optional(),
    ...nameFields,
    objektAdresse: z.string().optional(),
    portalUrl: z.string().url().optional(),
})
    .strict();
/**
 * A shooting was confirmed → n8n schedules the reminders (e.g. 24h / 1h before)
 * and the confirmation mail. PII-minimal; the reminder times derive from `at`.
 */
export const shootingConfirmedSchema = z
    .object({
    mandateId: z.string().uuid(),
    shootingId: z.string().uuid(),
    at: z.string().datetime(),
    durationMin: z.number().int().positive(),
    email: z.string().email().optional(),
    ...nameFields,
    objektAdresse: z.string().optional(),
})
    .strict();
/**
 * A portal customer proposed a checklist correction (F6 approval gate). INTERNAL
 * advisor notification → n8n templates the "neuer Korrekturvorschlag" mail to the
 * responsible makler. The canonical checklist is NEVER touched by this event —
 * staff approve/reject the pending change in the CRM. PII-minimal: the section +
 * mandate id (+ optional title/note) and the makler's user id so n8n can resolve
 * the recipient without a DB round-trip.
 */
export const mandateChangeProposedSchema = z
    .object({
    mandateId: z.string().uuid(),
    changeRequestId: z.string().uuid(),
    section: checklistSectionSchema,
    mandateTitle: z.string().optional(),
    note: z.string().optional(),
    /** The responsible makler (owner-user of the mandate), if assigned. */
    assignedUserId: z.string().uuid().optional(),
})
    .strict();
/**
 * A staff calendar event was created and staff were invited (WS-2). Stub seam:
 * n8n wiring (invite mails / reminders) is an owner-later step, like the other
 * pipeline events. PII-minimal but carries the attendee emails so a future n8n
 * flow can template the invitation without a DB round-trip.
 */
export const calendarEventInvitedSchema = z
    .object({
    eventId: z.string().uuid(),
    type: calendarEventTypeSchema,
    title: z.string(),
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    location: z.string().optional(),
    organizerEmail: z.string().email().optional(),
    attendees: z.array(z.object({ email: z.string().email(), name: z.string().optional() }).strict()),
})
    .strict();
/**
 * BREAK-GLASS (C6-13, v2.2 §11.4 "notification to data/security owner").
 *
 * ── THE PAYLOAD IS A POSITIVE GATE, AND IT IS THE POINT OF THIS SCHEMA ─────
 * Every field below is an id, an enum member, a timestamp or a count. There is
 * NO free text, and specifically there is no `grund` — the one field of a
 * break-glass grant a human writes, under time pressure, during an incident, is
 * the one field most likely to contain a customer name, a case reference or a
 * credential somebody pasted while explaining themselves.
 *
 * That makes this the inverse of `durableCopy`'s scrub in
 * `events/scrubbed-envelopes.ts`, which subtracts two known-dangerous fields
 * from an otherwise faithful copy. Subtracting is the right shape when the
 * payload is a business record whose fields are all known; it is the wrong
 * shape here, because the dangerous field is the one whose CONTENT is unknown
 * by construction. So this schema enumerates what MAY leave and the reason is
 * simply not on the list — it stays in `break_glass_grants.grund`, in the audit
 * row, and on the review screen, all three of which are behind the platform
 * grant.
 *
 * `grundLaenge` travels instead: it is what a recipient can actually act on
 * ("somebody broke glass and typed 31 characters" reads differently from "…and
 * wrote a paragraph") without the reason itself crossing the boundary into n8n,
 * a mail body and an inbox.
 *
 * The consequence is deliberate: n8n CANNOT template the reason into the
 * notification mail. The mail says a grant exists, who holds it, what it
 * unlocks and until when, and points at `/control/break-glass` — where the
 * reader is authenticated, MFA-verified and platform-scoped, and where reading
 * it is itself an audited act.
 */
export const breakGlassGrantedSchema = z
    .object({
    grantId: z.string().uuid(),
    /** WHO — an id, resolvable by anybody entitled to resolve it, by nobody else. */
    angefordertVonUserId: z.string().uuid(),
    /** WHAT — register keys from `BREAK_GLASS_SCOPES`, a closed vocabulary. */
    scope: z.array(z.string()),
    /** UNTIL WHEN — the server-computed expiry, so a recipient can see the clock. */
    laeuftAbAm: z.string().datetime(),
    /** HOW MUCH was written, never WHAT. See the block comment above. */
    grundLaenge: z.number().int().nonnegative(),
})
    // `.strict()` IS THE GATE, and without it the paragraph above was a
    // description rather than a mechanism (review #319, MAJOR-1). A plain
    // `z.object` STRIPS an unknown key and still reports `success: true`, and
    // `EventsService.emit` serialises the ORIGINAL envelope
    // (`JSON.stringify(envelope)`), not the parsed copy — so `refineEnvelope`,
    // which only reads `safeParse().success`, would have waved through an added
    // field and `emit` would have sent it. The review demonstrated it: an envelope
    // carrying `data.grund = <free text>` validated AND appeared verbatim in the
    // signed body. A RENAMED field was caught; an ADDED one was not, and adding
    // one is a single line at the emit site.
    //
    // This is the only class of payload in the catalogue where that difference is
    // load-bearing: the whole claim of the block comment above is about what may
    // NOT travel, and `EVENT_CATALOG` leans on it by classifying both types
    // `internal`. Repo-wide, none of the ~20 event payload schemas is strict while
    // `schemas/*.ts` uses it fourteen times — closing that gap is its own change,
    // deliberately not this one.
    .strict();
/**
 * A grant reached its expiry with no post-use review (C6-13, §11.4 "mandatory
 * post-use review"; verkenning risico 10 — "break-glass zonder na-review is
 * theater").
 *
 * A SECOND TYPE rather than a discriminator on the first, because the two are
 * different messages to possibly different people: the first is a notice ("this
 * is happening"), this one is an escalation ("this was not closed out"). One
 * type with an `anlass` field would make that difference a branch inside the
 * n8n router — i.e. a routing decision expressed in a place the event catalogue
 * cannot see, which is exactly what `EVENT_CATALOG.consumers` exists to make
 * visible.
 *
 * Same positive gate, same absent reason, plus the two facts that make it
 * actionable: how long the window was open and whether it was used at all. A
 * grant that expired UNUSED and unreviewed is a different conversation from one
 * that was used eleven times.
 */
export const breakGlassExpiredSchema = z
    .object({
    grantId: z.string().uuid(),
    angefordertVonUserId: z.string().uuid(),
    scope: z.array(z.string()),
    laufAbGelaufenAm: z.string().datetime(),
    /** How often the grant was actually used before it lapsed. Zero is a real answer. */
    nutzungen: z.number().int().nonnegative(),
})
    /** `.strict()` for the reason its twin above spells out — same gate, same payload class. */
    .strict();
/**
 * A display token was minted (Leitstand L3, §11.4-style notification for a new
 * NON-HUMAN credential). A display token is a new door into the estate — narrow,
 * read-only, but a credential — so its creation is worth a security notice, the
 * same shape break-glass uses.
 *
 * SAME POSITIVE GATE, SAME ABSENT FREE TEXT. Every field is an id, a timestamp
 * or a count. The device NAME is operator-typed, so — exactly like a break-glass
 * `grund` — only its LENGTH travels; the name itself stays in
 * `display_tokens.name`, in the audit row and on `/control/display-tokens`, all
 * behind the platform grant. And the SECRET and its HASH are absent by
 * construction: they are never in a payload, a log or an event, which is the one
 * hard rule of this whole step.
 */
export const displayTokenErstelltSchema = z
    .object({
    displayTokenId: z.string().uuid(),
    /** WHO minted it — an id, resolvable only by somebody entitled to. */
    erstelltVonUserId: z.string().uuid(),
    /** UNTIL WHEN it is usable — the server-computed expiry. */
    laeuftAbAm: z.string().datetime(),
    /** HOW MUCH name was typed, never WHAT. See the block comment above. */
    nameLaenge: z.number().int().nonnegative(),
})
    /** `.strict()` IS the gate — see `breakGlassGrantedSchema` for why a plain object is not. */
    .strict();
/**
 * A display token was revoked. The escalation-free twin of the mint notice: a
 * credential was withdrawn, and the wall it drove goes dark on its next request
 * (revocation is checked live at every resolve).
 */
export const displayTokenWiderrufenSchema = z
    .object({
    displayTokenId: z.string().uuid(),
    /** WHO revoked it, and WHO had minted it — two ids, no names. */
    widerrufenVonUserId: z.string().uuid(),
    erstelltVonUserId: z.string().uuid(),
    nameLaenge: z.number().int().nonnegative(),
})
    .strict();
// ── Staff identity lifecycle (MGOS fase 6 / C6-10) ───────────────────────────
/**
 * The canonical group-lifecycle events the verkenning (§9, `FASE4:27`) said
 * were "vandaag nul": a staff identity was created, changed, or shut down. They
 * are the DURABLE, catalogued twin of the in-process F2c-2 seam
 * (`common/user-lifecycle.ts`) — that seam drives the immediate ONYX and
 * Cloudflare-Access syncs, THESE are the record on the outbox that the group
 * event bus and the Control Center's identity view read.
 *
 * POSITIVE GATE, like the two break-glass payloads above and for the same
 * reason. Every field is an id, an address, an enum member or a boolean; there
 * is deliberately NO password, hash, session token or free-text field, and
 * `.strict()` makes a surplus one a REFUSAL at emit time rather than a secret
 * that travels to n8n and into the durable `webhook_events` row verbatim. A
 * staff account is exactly the kind of record whose adjacent fields (the
 * password hash, the TOTP secret) are the ones that must never cross this line,
 * so the schema enumerates what MAY leave and nothing else is on the list.
 *
 * The tenant-scoped `AdvisorRole` is intentionally NOT carried: it is a
 * per-assignment fact of the CRM, while this seam is the GROUP identity
 * lifecycle (§11.2 joiner/mover/leaver). `verwaltungRolle` IS carried because it
 * is the group-level grant the F2c-2 seam already propagates, and it is typed
 * `z.string()` (not the closed enum) on purpose — a wire contract that threw on
 * a future role value would fail an admin's own user-administration request,
 * which is the one thing an emit must never do (`nameFields` comment).
 */
export const userCreatedSchema = z
    .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    /** Always true on creation; carried so created/updated share one shape. */
    isActive: z.boolean(),
    /** The Verwaltung (ONYX) grant, or null. Opaque string — see block comment. */
    verwaltungRolle: z.string().nullable(),
})
    .strict();
/**
 * A staff account was handed a single-use link to set its own password.
 *
 * FOUR FIELDS, AND THE LIST IS THE ARGUMENT. Who (`userId`), where to send it
 * (`email`), what to send (`actionUrl`), until when it works (`expiresAt`).
 * Nothing else — no display name, no role, no `AdvisorRole`, no tenant slugs, no
 * `verwaltungRolle`. This payload is deliberately POORER than `user.created`
 * directly above, which carries `isActive` and `verwaltungRolle` because a
 * lifecycle listener acts on them; a mail-out has no use for either, and a field
 * no consumer uses is a field that exists only to leak. `.strict()` makes a
 * fifth one a refusal at emit time rather than a surprise in `webhook_events`.
 *
 * `actionUrl` IS THE CREDENTIAL, and naming it that is what protects it. This
 * field name is `SINGLE_USE_LINK_FIELD` in `events/scrubbed-envelopes.ts`, so
 * declaring it here automatically (a) puts this type in
 * `SCRUBBED_ENVELOPE_TYPES`, (b) redacts the durable copy to `token=REDACTED`,
 * and (c) makes the outbox drain refuse to replay the row — a re-delivery would
 * post a dead link to a colleague. That is why the token travels as a URL under
 * this exact name rather than as a `token` field of its own: the estate's
 * machinery recognises the name, and would not recognise a new one.
 *
 * WHICH ALSO FIXES THE CATALOGUE ENTRY at `highly_confidential`.
 * `HIGHLY_CONFIDENTIAL_TYPES` must equal exactly the scrubbed set, so the
 * classification is not a judgement call here — it is forced by the field, and
 * `event-catalog.router.test.ts` fails the build if the two disagree.
 */
export const userInvitedSchema = z
    .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    /** Full single-use link the backend already built (n8n only templates it). */
    actionUrl: z.string().url(),
    expiresAt: z.string().datetime(),
})
    .strict();
/**
 * «Passwort vergessen» for STAFF (Kommunikation PR-2): the same shape as the
 * invitation — user, address, the single-use link the backend already built,
 * and its expiry. Nothing else, on purpose: no name, no role, no tenant list.
 */
export const userPasswordResetSchema = z
    .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    /** Full single-use link the backend already built (n8n only templates it). */
    actionUrl: z.string().url(),
    expiresAt: z.string().datetime(),
})
    .strict();
/** A staff identity changed while staying active (role-in-Verwaltung, reactivation). */
export const userUpdatedSchema = z
    .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    isActive: z.boolean(),
    verwaltungRolle: z.string().nullable(),
})
    .strict();
/**
 * A staff identity was shut down — the offboarding record.
 *
 * `mode` distinguishes the two paths the F2c-2 seam already carries: a
 * DEACTIVATION leaves the `users` row in place (self-healing via the nightly
 * reconciliations), a hard DELETE removes it. A reader acting on this event
 * needs to know which, because a deleted row can no longer be looked up — and
 * the manual step the leaver notice DOES ask for (the `verwaltung.finavest.ch`
 * domain rule; the CRM door closes itself via the live Access-Sync) is one where
 * that lookup would otherwise be the natural next move. No
 * `verwaltungRolle`/`isActive`: after this event the only true answer to both is
 * "none", and `mode` says more than either would.
 */
export const userDeactivatedSchema = z
    .object({
    userId: z.string().uuid(),
    email: z.string().email(),
    mode: z.enum(['deactivated', 'deleted']),
})
    .strict();
// ── DRIP feedback loop (DF-13, ontwerp §9.4) ─────────────────────────────────
/**
 * A new point entered the feedback channel — the ONE wire event of the loop,
 * and its payload rule is the §9.4 sentence verbatim: "payload draagt id's en
 * lengtes". The melder's words (titel, text) are discovery free text about a
 * dochter's daily work; they live behind DRIP's own access control and NEVER
 * ride the bus — the durable `webhook_events` copy and the n8n mail body are
 * exactly the two places D-D exists to keep them out of. The LENGTHS travel
 * instead (the display-token precedent): enough for a notice to say "a short
 * wish" vs "three pages", nothing of the content.
 *
 * `art` is the closed §6.1 seven-word vocabulary (safe: chosen from buttons,
 * never typed); `herkunft` says which door it came through — the participant
 * surface or the staff intake — because the §9.4 mail's whole point is the
 * dochter's voice arriving, and a notice that cannot tell the two apart would
 * bury that signal under MLG's own typing.
 *
 * `.strict()` is the gate, as everywhere on this file: a future field must be
 * DECIDED here, not discovered in a mail.
 */
export const dripEingabeErstelltSchema = z
    .object({
    eingabeId: z.string().uuid(),
    dossierId: z.string().uuid(),
    art: z.enum([
        'wunsch_programm',
        'wunsch_funktion',
        'wunsch_ablauf',
        'fehler',
        'unklar',
        'fehlt',
        'frage',
    ]),
    herkunft: z.enum(['teilnehmer', 'mlg']),
    // Review #377 MINOR-3: unbounded, these were integers that HAPPEN to be
    // lengths. A ceiling says formally what the prose promises — a length is a
    // signal about size, never a channel: no counter here can grow into a
    // place to encode something.
    //
    // The number is the LARGEST DRIP free-text limit (`text`, 10 000 in
    // `dripEingabeCreateSchema`), used for BOTH fields on purpose instead of
    // each field's own limit. Coupling `titelLaenge` tightly to the 500 of
    // `freierText(500)` would mean that widening the title bound in the backend
    // DTO turns a legal intake into a rejected event INSIDE the transaction —
    // a 500 for the reporter, out of a ceiling nobody remembered. This bound
    // cannot do that: loose enough to survive a widened field, tight enough to
    // still be a bound.
    //
    // It also only holds because the producers count CODE POINTS
    // (`zeichenLaenge`), so an emoji-heavy title cannot report MORE characters
    // than the DTO let through.
    titelLaenge: z.number().int().nonnegative().max(10_000),
    textLaenge: z.number().int().nonnegative().max(10_000),
})
    .strict();
// ── Vermarktung A-4: de exposé-mailgate ──────────────────────────────────────
/**
 * Iemand vroeg op een publieke listing de Verkaufsdokumentation aan (ontwerp
 * §2.8). De mail E10 is de hele reden dat dit event bestaat: hij draagt de
 * tijdgebonden link naar de tokenpagina, en dus GEEN bijlage — een bijlage is
 * onherroepelijk, een link sterft met het token of met het object.
 *
 * `actionUrl` IS DE SLEUTELNAAM EN DAT IS GEEN SMAAKKWESTIE. `SCRUBBED_ENVELOPE_TYPES`
 * leidt de scrub-verplichting AF uit de schema-vorm (`scrubbed-envelopes.ts`,
 * `SINGLE_USE_LINK_FIELD`), en `event-catalog.router.test.ts` eist dat die
 * afgeleide verzameling exact gelijk is aan de `highly_confidential`-rijen van
 * de catalogus. Het veld anders noemen zou de duurzame `webhook_events`-kopie
 * een LEVEND credential laten dragen, en de suite zou dat vinden — precies
 * zoals bedoeld.
 *
 * `objektTitel` is de publieke titel van de listing, hetzelfde veld dat al op
 * de feed staat: de mail moet kunnen zeggen WELK object werd aangevraagd. Geen
 * adres, geen prijs — wat de mail nodig heeft en niets meer.
 *
 * `.strict()`, zoals overal op dit bestand: een volgend veld is een BESLUIT
 * hier, geen ontdekking in een inbox.
 */
export const exposeRequestedSchema = z
    .object({
    tenant: tenantSchema,
    leadId: z.string().uuid(),
    objektId: z.string().uuid(),
    /** De publieke sleutel van de listing — de router logt hem, de mail niet. */
    slug: z.string(),
    email: z.string().email(),
    ...nameFields,
    objektTitel: z.string().optional(),
    /** De volledige link naar de tokenpagina; het token rijdt in het FRAGMENT. */
    actionUrl: z.string().url(),
    /** Hele dagen dat de link werkt, zoals de MAIL het zegt — nooit een literal. */
    gueltigTage: z.number().int().positive(),
})
    .strict();
/**
 * Vor-Ort-Bewertung («Immobilie verkaufen», docs/VOR-ORT-FORMULIER-CONTRACT.md
 * §5.1): a seller asked for a free on-site valuation. Carries what the two mails
 * need and nothing more:
 *   - the choices as their technical WORDS (the router renders the labels);
 *   - name and e-mail for the confirmation to the requester (Mail 1);
 *   - NO telephone number: the broker opens the lead through `leadId` and finds
 *     it there — it never lands in a mailbox (contract §8 question 4).
 * The form fields are `vorname`/`nachname`; the backend maps them onto the
 * house names `firstName`/`lastName` that the router helpers read.
 *
 * `.strict()`, as everywhere in this file: a next field is a DECISION here.
 */
export const vorOrtRequestedSchema = z
    .object({
    tenant: tenantSchema,
    leadId: z.string().uuid(),
    objektart: z.enum(VOR_ORT_OBJEKTARTEN),
    plz: z.string(),
    zeitraum: z.enum(VOR_ORT_ZEITRAEUME),
    erreichbarkeit: z.enum(VOR_ORT_ERREICHBARKEITEN).optional(),
    ...nameFields,
    email: z.string().email(),
})
    .strict();
/**
 * Order E1 (17-10) §1.7: `objekt_anfrage.requested` — PII-VRIJ BY CONSTRUCTION.
 *
 * Exact de zes velden van de order en niets meer: geen naam, geen e-mail, geen
 * telefoon, geen nachricht, geen titel of slug. De router vraagt het register
 * om E12 (`e12.objekt_anfrage_bestaetigung`) en de teammail
 * (`intern.objekt_anfrage`) met alleen `anfrageId`/`leadId` als verwijzing; de
 * render-service leest de rij, het object en de ontvanger zelf uit de database
 * (B6-vorm). Zo reist een meerregelige nachricht nooit door een
 * event-parameter (geen CR/LF, max. 2000, HTML-escaped) en staat er in
 * `webhook_events` geen persoonsgegeven — de duurzame kopie is dus getrouw en
 * blijft door de outbox herhaalbaar.
 *
 * `istTest` staat erin omdat de order hem noemt; de producent emit voor een
 * testaanvraag niets, dus in de praktijk draagt elk event `false`.
 *
 * `.strict()`, zoals overal in dit bestand: een volgend veld is een BESLUIT.
 */
export const objektAnfrageRequestedSchema = z
    .object({
    tenantId: z.string().uuid(),
    anfrageId: z.string().uuid(),
    objektId: z.string().uuid(),
    leadId: z.string().uuid(),
    anliegen: z.enum(OBJEKT_ANFRAGE_ANLIEGEN),
    istTest: z.boolean(),
})
    .strict();
/** type → payload schema, for producers and the n8n-side validator. */
export const eventPayloadSchemas = {
    'lead.created': leadCreatedSchema,
    'result.email': resultEmailSchema,
    'consult.requested': consultRequestedSchema,
    'portal.invited': portalInvitedSchema,
    'auth.password_reset': authPasswordResetSchema,
    'auth.login_alert': authLoginAlertSchema,
    'dossier.completed': dossierCompletedSchema,
    'reminder.due': reminderDueSchema,
    'rates.stale': ratesStaleSchema,
    'rates.scrape_failed': ratesScrapeFailedSchema,
    'rates.scrape_anomaly': ratesScrapeAnomalySchema,
    'valuation.lead.created': valuationLeadCreatedSchema,
    'valuation.estimated': valuationEstimatedSchema,
    'valuation.sms_budget_exceeded': valuationSmsBudgetExceededSchema,
    'bewertung.uebernommen': bewertungUebernommenSchema,
    'lead.report_reminder': leadReportReminderSchema,
    'booking.created': bookingCreatedSchema,
    'contract.sent': contractSentSchema,
    'mandate.signed': mandateSignedSchema,
    'shooting.proposed': shootingProposedSchema,
    'shooting.confirmed': shootingConfirmedSchema,
    'mandate_change.proposed': mandateChangeProposedSchema,
    'calendar.event_invited': calendarEventInvitedSchema,
    'break_glass.granted': breakGlassGrantedSchema,
    'break_glass.expired': breakGlassExpiredSchema,
    'display_token.erstellt': displayTokenErstelltSchema,
    'display_token.widerrufen': displayTokenWiderrufenSchema,
    'user.created': userCreatedSchema,
    'user.invited': userInvitedSchema,
    'user.password_reset': userPasswordResetSchema,
    'user.updated': userUpdatedSchema,
    'user.deactivated': userDeactivatedSchema,
    'drip.eingabe.erstellt': dripEingabeErstelltSchema,
    'expose.requested': exposeRequestedSchema,
    'vor_ort.requested': vorOrtRequestedSchema,
    'objekt_anfrage.requested': objektAnfrageRequestedSchema,
};
// ─────────────────────────────────────────────────────────────────────────────
// Envelope v2 (MGOS fase 4, P4-4 — docs/architecture/FASE4-EVENTS-AUDIT-DLQ.md §5)
// ─────────────────────────────────────────────────────────────────────────────
//
// v2 is PURELY ADDITIVE. Every v1 field keeps its name, its type and its
// meaning; v2 only puts more next to them. That is not politeness, it is the
// only shape that is safe here, for three reasons the plan calls out as hard
// compatibility constraints:
//
//  1. `type` NEVER changes. The single consumer — the n8n router
//     (`infra/n8n/workflows/finavest-event-router.json`) — matches it against a
//     hard allowlist of 18 literal strings. Renaming a type does not degrade
//     gracefully: every event of that type turns into an HTTP 422, and events
//     that 422 are deliberately NOT retried (see EventsService.isRetryable), so
//     the loss is silent and permanent. The canonical v2.2 name therefore
//     travels BESIDE it as `canonicalType`, which the router ignores today and
//     can learn in its own reversible step.
//  2. Extra top-level fields cost bytes, and the receiving webhook is capped at
//     N8N_PAYLOAD_SIZE_MAX = 1 MiB (infra/compose.yaml). The v2 header adds a
//     bounded ~250 bytes; the measurement lives in events.envelope-v2.test.ts.
//  3. Stored outbox rows (`webhook_events.payload`) are DATA, not code. They
//     are replayed byte-for-byte and are never rewritten — a backfill would
//     both invalidate the audit copy and hand the replay tool a body the
//     original signature never covered. So v1 rows must keep validating
//     forever: `eventEnvelopeV1Schema` reads them, `eventEnvelopeV2Schema`
//     writes new ones, and `eventEnvelopeReadSchema` is the union used wherever
//     an envelope of unknown vintage is read.
/**
 * Envelope version written by every new emit. v1 rows simply have no such field.
 *
 * A MINOR bump, and a numeric one (P4-9). v2.1 adds three optional-to-read
 * fields and removes nothing, which is exactly what a minor version means — so
 * expressing it as `2.1` says "read me as a v2, there is simply more of me"
 * while `3` would say "assume nothing". Numeric rather than the string `'2.1'`
 * so `envelopeVersion >= 2` keeps working for any consumer that compares, and
 * so the field's TYPE never changed after it shipped.
 *
 * The one caveat, written down because it is the trap: `2.10` would equal `2.1`
 * as a number. There will be no 2.10 — the next step is 2.2, and after 2.9 the
 * answer is 3.
 */
export const ENVELOPE_VERSION = 2.1;
/**
 * The previous minor. Kept as a named constant because it is not history: rows
 * written with it sit in `webhook_events` and are replayed byte-for-byte, so
 * "2" stays a value this contract must recognise forever.
 */
export const ENVELOPE_VERSION_V2 = 2;
/**
 * Who caused the event.
 *
 * The vocabulary mirrors Prisma's `PrincipalKind` (customer | user | system)
 * plus `service`, ON PURPOSE: `actor` exists to be the join key between an
 * event and the `audit_logs` rows of the same journey (§5). If the envelope
 * said `service` where the audit row of the very same request says `system`,
 * that join would silently produce two unrelated-looking halves of one story.
 * So `service` is part of the wire contract but nothing emits it yet — a real
 * machine principal (fase 5 service accounts) must land in BOTH sinks at once.
 *
 * v2.2 §15.2 uses `human|service|automation|agent`; we deliberately keep our
 * own three-value core so the join with audit stays an equality check, and map
 * to the v2.2 vocabulary at the group-ingestion boundary when that exists.
 */
export const eventActorKindSchema = z.enum(['user', 'customer', 'system', 'service']);
export const eventActorSchema = z.object({
    kind: eventActorKindSchema,
    /** userId / customerAccountId. Absent for `system` — there is no id to give. */
    id: z.string().uuid().optional(),
    /**
     * The principal the actor acted FOR (staff acting on a customer's behalf).
     * Reserved: this platform has no impersonation, so nothing sets it today.
     */
    onBehalfOf: z.string().uuid().optional(),
});
/**
 * The thing the event is about. Deliberately a free-form `id` STRING rather
 * than a uuid: not every subject has one (a rate source is keyed by
 * `providerKey`), and an envelope must never fail to emit over its own metadata.
 *
 * NOTE: this is the same idea as the long-existing but never-written
 * `webhook_events.relatedType/relatedId` column pair. Those columns stay empty:
 * `relatedId` is `@db.Uuid`, so writing a non-uuid subject key into it would
 * make the INSERT throw — inside `emit()`, i.e. on the originating request path.
 * The envelope is the safe place for this, the columns are not.
 */
export const eventObjectRefSchema = z.object({
    type: z.string().min(1),
    id: z.string().min(1),
});
/**
 * Which deployable produced the event. `platform` is the Finavest/MLG backend —
 * one deployable that serves several logical domains, which is why the DOMAIN
 * lives in `canonicalType` (`brokerage.…`, `finavest.…`) and not here.
 * `version` is the promoted release sha (APP_VERSION, written by promote.sh),
 * falling back to `dev` on a non-promoted stack.
 */
export const eventProducerSchema = z.object({
    application: z.literal('platform'),
    version: z.string().min(1),
});
/**
 * Payload-schema version PER TYPE (§15.2 `payload_schema`).
 *
 * All 1 today: no payload has ever had a breaking change — every extension so
 * far was an optional field, which is exactly the additive discipline the
 * comment on `nameFields` describes. The table exists so the FIRST breaking
 * payload change has somewhere to be recorded instead of being invisible.
 */
export const EVENT_SCHEMA_VERSIONS = {
    'lead.created': 1,
    'result.email': 1,
    'consult.requested': 1,
    'portal.invited': 1,
    'auth.password_reset': 1,
    'auth.login_alert': 1,
    'dossier.completed': 1,
    'reminder.due': 1,
    'rates.stale': 1,
    'rates.scrape_failed': 1,
    'rates.scrape_anomaly': 1,
    'valuation.lead.created': 1,
    'valuation.estimated': 1,
    'valuation.sms_budget_exceeded': 1,
    'bewertung.uebernommen': 1,
    'lead.report_reminder': 1,
    'booking.created': 1,
    'contract.sent': 1,
    'mandate.signed': 1,
    'shooting.proposed': 1,
    'shooting.confirmed': 1,
    'mandate_change.proposed': 1,
    'calendar.event_invited': 1,
    'break_glass.granted': 1,
    'break_glass.expired': 1,
    'display_token.erstellt': 1,
    'display_token.widerrufen': 1,
    'user.created': 1,
    'user.invited': 1,
    'user.password_reset': 1,
    'user.updated': 1,
    'user.deactivated': 1,
    'drip.eingabe.erstellt': 1,
    'expose.requested': 1,
    'vor_ort.requested': 1,
    'objekt_anfrage.requested': 1,
};
/**
 * Wire type → canonical v2.2 name (`<application>.<object>.<verb>.v<n>`,
 * Appendix C). ADDITIVE ONLY: the router never reads this field, so getting a
 * name wrong here cannot break delivery — which is precisely why the canonical
 * naming can be introduced now and adopted later, instead of the other way
 * round (renaming `type` first would be a one-way door: every in-flight event
 * would 422, and a 422 is never retried).
 *
 * `Record<EventType, …>` makes a missing entry a COMPILE error; the test suite
 * additionally asserts the shape and uniqueness at runtime, so a new type
 * cannot slip through with an empty or duplicated canonical name either.
 */
export const EVENT_CANONICAL_TYPES = {
    'lead.created': 'finavest.lead.created.v1',
    'result.email': 'finavest.calculator_result.requested.v1',
    'consult.requested': 'finavest.consultation.requested.v1',
    'portal.invited': 'identity.portal_invite.issued.v1',
    'auth.password_reset': 'identity.password_reset.requested.v1',
    'auth.login_alert': 'security.login_alert.raised.v1',
    'dossier.completed': 'finavest.dossier.completed.v1',
    'reminder.due': 'workflow.reminder.due.v1',
    'rates.stale': 'finavest.rate_sheet.stale.v1',
    'rates.scrape_failed': 'finavest.rate_scrape.failed.v1',
    'rates.scrape_anomaly': 'finavest.rate_scrape.anomaly_detected.v1',
    'valuation.lead.created': 'brokerage.valuation_lead.created.v1',
    'valuation.estimated': 'brokerage.valuation.estimated.v1',
    'valuation.sms_budget_exceeded': 'brokerage.valuation_sms_budget.exceeded.v1',
    'bewertung.uebernommen': 'brokerage.valuation_dossier.adopted.v1',
    'lead.report_reminder': 'brokerage.valuation_report.reminder.v1',
    'booking.created': 'brokerage.booking.created.v1',
    'contract.sent': 'brokerage.mandate_contract.sent.v1',
    'mandate.signed': 'brokerage.mandate.signed.v1',
    'shooting.proposed': 'brokerage.photo_shooting.proposed.v1',
    'shooting.confirmed': 'brokerage.photo_shooting.confirmed.v1',
    'mandate_change.proposed': 'brokerage.mandate_change.proposed.v1',
    'calendar.event_invited': 'workflow.calendar_event.invited.v1',
    // `security.`, the domain `auth.login_alert` already uses, and not `control.`:
    // the canonical name says which STORY a §19.4 review reads the row under, and
    // emergency access to masked material is a security story wherever the button
    // happens to live.
    'break_glass.granted': 'security.break_glass.granted.v1',
    'break_glass.expired': 'security.break_glass.expired_unreviewed.v1',
    // `security.`, the domain break-glass and login-alert already use: the
    // lifecycle of a CREDENTIAL is a security story a §19.4 review reads under
    // that heading, wherever the button that mints it happens to live.
    'display_token.erstellt': 'security.display_token.issued.v1',
    'display_token.widerrufen': 'security.display_token.revoked.v1',
    // `identity.`, the domain `portal.invited` and `auth.password_reset` already
    // use: a staff joiner/mover/leaver is an identity story wherever it is raised.
    'user.created': 'identity.user.created.v1',
    'user.invited': 'identity.user.invited.v1',
    'user.password_reset': 'identity.user.password_reset.requested.v1',
    'user.updated': 'identity.user.updated.v1',
    'user.deactivated': 'identity.user.deactivated.v1',
    // `drip.`, its own domain: the governance instrument's channel FROM the
    // daughters — an operations story, not identity and not a product sale.
    'drip.eingabe.erstellt': 'drip.eingabe.submitted.v1',
    // `brokerage.`, het domein dat de hele Vermarktungs-familie voert: het
    // opvragen van een Verkaufsdokumentation is een makelaarsverhaal, en het
    // onderwerp is de AANVRAAG en niet het object waarover ze gaat.
    'expose.requested': 'brokerage.expose.requested.v1',
    // A seller asking for a valuation visit is brokerage work, like the exposé request.
    'vor_ort.requested': 'brokerage.vor_ort_bewertung.requested.v1',
    // Een koper die een besichtiging of de unterlagen vraagt: makelaarswerk, het
    // onderwerp is de AANVRAAG (dezelfde lijn als `expose.requested`).
    'objekt_anfrage.requested': 'brokerage.objekt_anfrage.requested.v1',
};
/**
 * type → how to name the subject of the event, read from its own payload.
 *
 * A function per type rather than a `{type, field}` table because two cases are
 * not a plain field read: `reminder.due` carries its subject KIND in the
 * payload (`targetType`), and `rates.stale` is a digest about several sources
 * at once and therefore has no single subject — it returns `undefined`, which
 * is why `object` is optional in the envelope. Guessing a subject there would
 * be worse than admitting there is none.
 */
const objectRefs = {
    'lead.created': (d) => ({ type: 'lead', id: d.leadId }),
    'result.email': (d) => ({ type: 'lead', id: d.leadId }),
    'consult.requested': (d) => d.leadId
        ? { type: 'lead', id: d.leadId }
        : d.customerAccountId
            ? { type: 'customer_account', id: d.customerAccountId }
            : undefined,
    'portal.invited': (d) => ({ type: 'customer_account', id: d.customerAccountId }),
    'auth.password_reset': (d) => ({ type: 'customer_account', id: d.customerAccountId }),
    'auth.login_alert': (d) => ({ type: 'user', id: d.userId }),
    'dossier.completed': (d) => ({ type: 'financing_request', id: d.financingRequestId }),
    'reminder.due': (d) => ({ type: d.targetType, id: d.targetId }),
    'rates.stale': () => undefined,
    'rates.scrape_failed': (d) => ({ type: 'rate_source', id: d.providerKey }),
    'rates.scrape_anomaly': (d) => ({ type: 'rate_source', id: d.providerKey }),
    'valuation.lead.created': (d) => ({ type: 'lead', id: d.leadId }),
    'valuation.estimated': (d) => ({ type: 'lead', id: d.leadId }),
    // A day-level counter alarm has no subject row to point at.
    'valuation.sms_budget_exceeded': () => undefined,
    'bewertung.uebernommen': (d) => ({ type: 'lead', id: d.leadId }),
    'lead.report_reminder': (d) => ({ type: 'lead', id: d.leadId }),
    'booking.created': (d) => ({ type: 'lead', id: d.leadId }),
    'contract.sent': (d) => ({ type: 'mandate', id: d.mandateId }),
    'mandate.signed': (d) => ({ type: 'mandate', id: d.mandateId }),
    'shooting.proposed': (d) => ({ type: 'shooting', id: d.shootingId }),
    'shooting.confirmed': (d) => ({ type: 'shooting', id: d.shootingId }),
    'mandate_change.proposed': (d) => ({ type: 'mandate_change_request', id: d.changeRequestId }),
    'calendar.event_invited': (d) => ({ type: 'calendar_event', id: d.eventId }),
    // The GRANT is the subject, not the person who asked for it. A reader
    // following the reference wants the row that carries the reason, the scope
    // and the review — the requester is one field on it.
    'break_glass.granted': (d) => ({ type: 'break_glass_grant', id: d.grantId }),
    'break_glass.expired': (d) => ({ type: 'break_glass_grant', id: d.grantId }),
    // The TOKEN is the subject, not the person who minted or revoked it: a reader
    // following the reference wants the credential's row, which carries the name,
    // the scope and the expiry — the actor is one field on the audit trail.
    'display_token.erstellt': (d) => ({ type: 'display_token', id: d.displayTokenId }),
    'display_token.widerrufen': (d) => ({ type: 'display_token', id: d.displayTokenId }),
    // The subject is the user, not the admin who made the change — the reader
    // following the reference wants the identity, of which the actor is a field.
    'user.created': (d) => ({ type: 'user', id: d.userId }),
    'user.invited': (d) => ({ type: 'user', id: d.userId }),
    'user.password_reset': (d) => ({ type: 'user', id: d.userId }),
    'user.updated': (d) => ({ type: 'user', id: d.userId }),
    'user.deactivated': (d) => ({ type: 'user', id: d.userId }),
    // The POINT is the subject — the reader following the reference wants the
    // eingabe row (the CRM detail), of which melder and dossier are fields.
    'drip.eingabe.erstellt': (d) => ({ type: 'drip_eingabe', id: d.eingabeId }),
    // De LEAD is het onderwerp, niet het object: een lezer die de verwijzing
    // volgt wil de persoon die het document vroeg (en van wie de aanvraag,
    // haar rijpheid en haar retentie feiten zijn). Het object is één veld op de
    // payload — dezelfde keuze als bij `booking.created`.
    'expose.requested': (d) => ({ type: 'lead', id: d.leadId }),
    'vor_ort.requested': (d) => ({ type: 'lead', id: d.leadId }),
    // De LEAD, zoals bij de exposé-aanvraag: wie de verwijzing volgt wil de persoon.
    'objekt_anfrage.requested': (d) => ({ type: 'lead', id: d.leadId }),
};
/**
 * The subject of an event, or `undefined` when it genuinely has none.
 *
 * Refuses to touch a non-object payload rather than throwing on it. A subject
 * reference is METADATA: it must never be the thing that decides whether an
 * event goes out. Malformed input is refused a few lines later by the envelope
 * schema, with an error that names the actual problem — not by a TypeError
 * raised while reading `.leadId` off `undefined`.
 */
export function eventObjectRef(type, data) {
    if (typeof data !== 'object' || data === null)
        return undefined;
    const resolve = objectRefs[type];
    return resolve(data);
}
/** The v1 fields, unchanged. Shared by both versions so they cannot drift. */
const envelopeCoreFields = {
    id: z.string().uuid(),
    type: eventTypeSchema,
    occurredAt: z.string().datetime(),
    tenantId: z.string().uuid(),
    data: z.record(z.string(), z.unknown()),
};
/**
 * READ schema for envelopes written before P4-4 — deliberately TOLERANT.
 *
 * It checks the envelope frame and stops there: no payload validation. That is
 * not laziness, it is a consequence of how the durable copy is built.
 * `EventsService.emit()` SCRUBS the stored copy on purpose — single-use links
 * become `token=REDACTED`, and the `valuation.estimated` amounts become `null`
 * (revDSG). A stored row therefore legitimately violates its own payload schema
 * (`estimate: z.number()` vs `null`), and a strict reader would declare years
 * of correct audit trail invalid.
 *
 * `looseObject` keeps unknown keys instead of stripping them: a reader that
 * uses the parsed output must not silently lose fields of a row it did not
 * write.
 */
export const eventEnvelopeV1Schema = z.looseObject(envelopeCoreFields);
/** Everything v2 added around the v1 core. Shared by v2 and v2.1 verbatim. */
const envelopeV2Fields = {
    ...envelopeCoreFields,
    canonicalType: z.string().min(1),
    schemaVersion: z.number().int().positive(),
    correlationId: z.string().uuid(),
    actor: eventActorSchema,
    producer: eventProducerSchema,
    object: eventObjectRefSchema.optional(),
};
/**
 * The three consistency rules both strict vintages enforce: the payload must
 * match its type's schema, and `canonicalType`/`schemaVersion` must match the
 * tables above — a typo is a refusal, not a mislabelled event.
 *
 * One function rather than one per version, because the day these two drift is
 * the day a v2.1 envelope is allowed to say something a v2 envelope may not.
 */
function refineEnvelope(env, ctx) {
    const res = eventPayloadSchemas[env.type].safeParse(env.data);
    if (!res.success) {
        ctx.addIssue({
            code: 'custom',
            message: `invalid payload for event ${env.type}`,
            path: ['data'],
        });
    }
    if (env.canonicalType !== EVENT_CANONICAL_TYPES[env.type]) {
        ctx.addIssue({
            code: 'custom',
            message: `canonicalType must be ${EVENT_CANONICAL_TYPES[env.type]} for ${env.type}`,
            path: ['canonicalType'],
        });
    }
    if (env.schemaVersion !== EVENT_SCHEMA_VERSIONS[env.type]) {
        ctx.addIssue({
            code: 'custom',
            message: `schemaVersion must be ${EVENT_SCHEMA_VERSIONS[env.type]} for ${env.type}`,
            path: ['schemaVersion'],
        });
    }
}
/**
 * Envelope v2 — no longer the write schema, and that is the whole point.
 *
 * P4-4 wrote these; P4-9 writes v2.1. Rows in this shape sit in
 * `webhook_events` and are replayed byte-for-byte, so v2 must keep validating
 * for as long as those rows are kept — which is why it is a schema of its own
 * rather than a `.partial()` of the newer one.
 */
export const eventEnvelopeV2Schema = z
    .object({ envelopeVersion: z.literal(ENVELOPE_VERSION_V2), ...envelopeV2Fields })
    .superRefine(refineEnvelope);
/**
 * Envelope v2.1 (P4-9) — the WRITE schema. Three additions, no removals.
 *
 * They are the §15.2 fields that were parked as "priority 3" in the plan (§5),
 * and each is here because it answers a question the v2 envelope cannot:
 *
 *  - `recordedAt` — WHEN WE wrote it down, as opposed to when it happened.
 *    Today the two coincide to the millisecond, because every producer emits
 *    synchronously with its own fact. They are two fields anyway, because they
 *    are two different claims: `occurredAt` is a statement about the WORLD that
 *    a producer may legitimately backdate (a signature carries its own
 *    `signedAt`), while `recordedAt` is a statement about OUR records that
 *    nothing may ever move. A single timestamp cannot be both, and the day the
 *    first producer backdates one, an envelope without `recordedAt` would have
 *    quietly lost the ability to say when we learned of it.
 *  - `causationId` — the id of the message that CAUSED this one, as opposed to
 *    the journey it belongs to (`correlationId`). RESERVED: nothing sets it,
 *    and honestly so — this platform has no event CONSUMER of its own (the one
 *    consumer is n8n; `webhook_events` is 100% outbound), so no event is
 *    currently born while handling another. The field is part of the wire
 *    contract now so that the first in-process consumer can fill it without a
 *    second version bump. Same discipline as `actor.onBehalfOf`.
 *  - `legalEntityId` — which legal entity (ADR-001 `organisations`) the tenant
 *    of this event belongs to. NOT reserved: `EventsService` resolves it from
 *    `tenants.organisationId`. Optional because that column is nullable by
 *    design — a tenant upserted during bootstrap has no organisation yet, and
 *    an envelope must never fail to emit over its own metadata.
 */
export const eventEnvelopeV21Schema = z
    .object({
    envelopeVersion: z.literal(ENVELOPE_VERSION),
    ...envelopeV2Fields,
    recordedAt: z.string().datetime(),
    causationId: z.string().uuid().optional(),
    legalEntityId: z.string().uuid().optional(),
})
    .superRefine(refineEnvelope);
/**
 * What a NEW event must satisfy. An alias on purpose: producers should say
 * "the current write contract", not name a vintage, so the next minor bump is
 * one line here instead of a search for every `V21`.
 */
export const eventEnvelopeWriteSchema = eventEnvelopeV21Schema;
/**
 * READ path for an envelope of unknown vintage — newest first, v1 last.
 *
 * All three vintages exist in `webhook_events` simultaneously and will for as
 * long as those rows are kept, so anything that READS an envelope (outbox
 * drain, replay, a future audit/Control-Center view) must use this union.
 * Nothing should validate an envelope it is about to WRITE with it. The v1
 * branch is `looseObject` and performs NO payload validation, so this union
 * accepts a v2.1 envelope with a broken payload — which is not a bug but the
 * requirement: `emit()` deliberately scrubs the durable copy (redacted tokens,
 * nulled amounts), so a correct stored row legitimately violates its own
 * payload schema and must still read. Use `eventEnvelopeWriteSchema` for
 * anything about to go out; mixing the two up is how a malformed envelope would
 * pass as "well, it parses as v1".
 *
 * ORDER: newest first, tolerant last. It does not change WHETHER a row is
 * accepted (v1 would take almost anything), it decides WHICH branch's shape a
 * caller gets back — so a well-formed new envelope is parsed as the vintage it
 * actually is instead of collapsing into the loose one.
 *
 * THAT SAME TOLERANCE IS WHAT MADE THE PAYLOADS SAFE TO TIGHTEN. Making every
 * payload strict changed what may be WRITTEN; it changed nothing about rows
 * already lying in `webhook_events`. A stored envelope carrying a key its type
 * no longer allows simply fails the two strict branches and reads as v1, exactly
 * like the scrubbed rows above — and no reader treats a failed parse as a reason
 * to withhold anyway: the outbox drain and the C6-4 replay both go through
 * `EventsService.deliver()`, whose union parse is an OBSERVATION that logs
 * `event_envelope_unrecognised` and delivers regardless, and the replay guard
 * (`events/scrubbed-envelopes.ts:replaySperre`) reasons about the TYPE and the
 * scrub, never about the payload schema. No row became unreplayable.
 */
export const eventEnvelopeReadSchema = z.union([
    eventEnvelopeV21Schema,
    eventEnvelopeV2Schema,
    eventEnvelopeV1Schema,
]);
